import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { SEO } from "@/components/seo/SEO";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <SEO
        title="Privacy Policy – Manifest"
        description="Privacy policy for Manifest. Learn how we protect your data and what information we collect."
        canonical="https://manifestme.me/privacy"
      />
      <div className="mx-auto max-w-2xl px-5 py-10 space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="min-h-[44px] min-w-[44px]">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-editorial text-2xl font-bold text-[#111]">Privacy Policy</h1>
        </div>

        <div className="rounded-xl border border-[#E5E5E0] bg-white p-6 shadow-sm space-y-6 text-sm text-[#111] leading-relaxed">
          <p className="text-xs text-[#9CA3AF]">Last updated: March 22, 2026</p>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Overview</h2>
            <p className="text-[#6B7280]">
              Manifest ("we," "us," or "our") is a personal asset management platform. This Privacy Policy explains how we collect, use, store, and protect your information when you use our service. We are committed to safeguarding your privacy and being transparent about our data practices.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Information We Collect</h2>
            <p className="text-[#6B7280]"><strong>Account information:</strong> When you create an account, we collect your email address and display name. If you sign in with Google or Apple, we receive your name and email from those providers.</p>
            <p className="text-[#6B7280]"><strong>Asset data:</strong> Information you provide about your belongings, including photos, receipts, serial numbers, purchase dates, prices, warranty details, and any documents you upload.</p>
            <p className="text-[#6B7280]"><strong>Email forwarding:</strong> If you use our receipt forwarding feature, we process incoming emails sent to your unique Manifest address to extract product and purchase information. We store the extracted data and any attached images. We do not read or store the full email body beyond what is needed for extraction.</p>
            <p className="text-[#6B7280]"><strong>Usage data:</strong> We collect basic usage information such as pages visited and features used to improve the service. We do not use third-party analytics trackers.</p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">How We Use Your Information</h2>
            <p className="text-[#6B7280]">We use your information solely to provide, maintain, and improve the Manifest service. Specifically:</p>
            <ul className="list-disc pl-5 space-y-1 text-[#6B7280]">
              <li>To store and organize your asset catalog</li>
              <li>To extract product details from photos, receipts, and emails using AI</li>
              <li>To check your products against recall databases</li>
              <li>To generate insurance reports and export documents</li>
              <li>To calculate estimated resale values</li>
              <li>To send you alerts about expiring warranties and return windows</li>
              <li>To process your subscription and payments (via Stripe)</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">AI Processing</h2>
            <p className="text-[#6B7280]">
              We use AI models to extract information from your photos, receipts, and documents. Image and text data is sent to our AI processing providers solely for the purpose of extraction. We do not use your data to train AI models. AI-processed data may contain inaccuracies — you should always verify extracted information.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Data Storage & Security</h2>
            <p className="text-[#6B7280]">
              Your data is stored on secure, encrypted infrastructure. All data is encrypted in transit using TLS. Files and documents are stored in private, access-controlled storage buckets. We implement row-level security to ensure you can only access your own data. We regularly review our security practices.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Data Sharing</h2>
            <p className="text-[#6B7280]">We do not sell, rent, or trade your personal information. We share data only with:</p>
            <ul className="list-disc pl-5 space-y-1 text-[#6B7280]">
              <li><strong>Stripe:</strong> For payment processing (email and subscription status only)</li>
              <li><strong>AI providers:</strong> For data extraction from your uploads (processed data only, not stored by them)</li>
              <li><strong>CPSC databases:</strong> Product identifiers only, to check for recalls (no personal information is shared)</li>
            </ul>
            <p className="text-[#6B7280]">We will never sell your data or use it for advertising.</p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Your Rights</h2>
            <p className="text-[#6B7280]">You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1 text-[#6B7280]">
              <li><strong>Access</strong> all data associated with your account</li>
              <li><strong>Export</strong> your data at any time via the insurance export or individual asset downloads</li>
              <li><strong>Delete</strong> your account and all associated data permanently from the Settings page</li>
              <li><strong>Correct</strong> any inaccurate information by editing your assets</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Cookies & Local Storage</h2>
            <p className="text-[#6B7280]">
              We use essential cookies and local storage for authentication and remembering your preferences (such as dismissed onboarding cards). We do not use tracking cookies or third-party advertising cookies.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Data Retention</h2>
            <p className="text-[#6B7280]">
              We retain your data for as long as your account is active. When you delete your account, all associated data — including assets, documents, files, and profile information — is permanently deleted. We do not retain backups of deleted accounts.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Children's Privacy</h2>
            <p className="text-[#6B7280]">
              Manifest is not intended for use by children under 13. We do not knowingly collect information from children under 13. If we learn that we have collected personal information from a child under 13, we will delete it promptly.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Changes to This Policy</h2>
            <p className="text-[#6B7280]">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice within the app. Your continued use of Manifest after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-base">Contact</h2>
            <p className="text-[#6B7280]">
              If you have questions about this Privacy Policy or your data, contact us at{" "}
              <a href="mailto:privacy@manifestme.me" className="text-[#0F766E] underline hover:text-[#065F46]">
                privacy@manifestme.me
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
